import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Alert,
  ActivityIndicator,
  Switch,
  Image,
  Platform,
} from 'react-native';
import Icon from '@expo/vector-icons/MaterialIcons';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { fonts } from '../utils/fonts';

// Helper function to show alerts on both web and mobile
const showAlert = (title: string, message?: string) => {
  if (Platform.OS === 'web') {
    window.alert(message ? `${title}\n${message}` : title);
  } else {
    Alert.alert(title, message);
  }
};

// Bank icons mapping
const bankIcons = {
  cbe: require('../../assets/banks/cbe.jpeg'),
  cbebirr: require('../../assets/banks/cbebirr.jpeg'),
  abyssinia: require('../../assets/banks/abyssinia.png'),
  dashen: require('../../assets/banks/dashen.png'),
  telebirr: require('../../assets/banks/telebirr.jpeg'),
  awash: require('../../assets/banks/awash.png'),
};

// Helper function to get bank icon key from bank name or ID
const getBankIconKey = (bankIdOrName: string | undefined | null): keyof typeof bankIcons | null => {
  if (!bankIdOrName) return null;
  const normalized = bankIdOrName.toLowerCase();
  
  if (normalized.includes('cbebirr') || normalized.includes('cbe birr')) return 'cbebirr';
  if (normalized.includes('commercial') || normalized.includes('cbe')) return 'cbe';
  if (normalized.includes('abyssinia')) return 'abyssinia';
  if (normalized.includes('dashen')) return 'dashen';
  if (normalized.includes('telebirr') || normalized.includes('tele birr')) return 'telebirr';
  if (normalized.includes('awash')) return 'awash';
  
  return null;
};

const colors = {
  beeYellow: '#F4C430',
  honeyGold: '#D4AF37',
  darkYellow: '#B8860B',
  deepNavy: '#1A1B3E',
  charcoal: '#2C2C2C',
  cream: '#FFF9E6',
  white: '#FFFFFF',
  warmGray: '#6B6B6B',
  lightGray: '#F5F5F5',
  softBlue: '#4A90E2',
  success: '#27AE60',
  danger: '#E74C3C',
};

interface Bank {
  id?: string;
  name: string;
  accountHolderName: string;
  accountNumber: string;
  isActive: boolean;
  icon?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

const BankConfigurationContent: React.FC<{ colors?: any; isDarkMode?: boolean }> = ({
  colors: customColors = colors,
  isDarkMode = false,
}) => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [formData, setFormData] = useState<Bank>({
    name: '',
    accountHolderName: '',
    accountNumber: '',
    isActive: true,
    icon: 'account-balance',
  });

  const predefinedBanks = [
    {
      name: 'Commercial Bank of Ethiopia',
      icon: 'account-balance',
    },
    {
      name: 'Abyssinia Bank',
      icon: 'account-balance',
    },
    {
      name: 'Dashen Bank',
      icon: 'account-balance',
    },
    {
      name: 'Tele Birr',
      icon: 'phone-android',
    },
    {
      name: 'Awash Bank',
      icon: 'account-balance',
    },
  ];

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading bank configurations (no cache - real-time data)...');
      
      // Using direct Firebase - always gets fresh data (no caching)
      const banksSnapshot = await getDocs(collection(db, 'banks'));
      
      console.log('📊 Raw bank documents:');
      const fetchedBanks = banksSnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log(`  - Bank ${doc.id}:`, {
          name: data.name,
          accountHolderName: data.accountHolderName,
          accountNumber: data.accountNumber,
          isActive: data.isActive,
          icon: data.icon
        });
        
        return {
          id: doc.id,
          name: data.name || '',
          accountHolderName: data.accountHolderName || '',
          accountNumber: data.accountNumber || '',
          isActive: data.isActive ?? true,
          icon: data.icon || 'account-balance',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as Bank;
      });
      
      console.log(`✅ Loaded ${fetchedBanks.length} bank configurations from Firestore`);
      setBanks(fetchedBanks);
    } catch (error) {
      console.error('Error fetching banks:', error);
      showAlert('Error', 'Failed to load bank configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showAlert('Validation Error', 'Bank name is required');
      return;
    }
    if (!formData.accountHolderName.trim()) {
      showAlert('Validation Error', 'Account holder name is required');
      return;
    }
    if (!formData.accountNumber.trim()) {
      showAlert('Validation Error', 'Account number is required');
      return;
    }

    try {
      setSaving(true);
      
      const bankData = {
        name: formData.name.trim(),
        accountHolderName: formData.accountHolderName.trim(),
        accountNumber: formData.accountNumber.trim(),
        isActive: formData.isActive,
        icon: formData.icon || 'account-balance',
        updatedAt: new Date(),
      };

      if (editingId) {
        // Update existing bank
        const bankRef = doc(db, 'banks', editingId);
        await updateDoc(bankRef, bankData);
        setBanks(
          banks.map((bank) =>
            bank.id === editingId ? { ...bank, ...bankData } : bank
          )
        );
        showAlert('Success', 'Bank configuration updated successfully');
      } else {
        // Add new bank
        const docRef = await addDoc(collection(db, 'banks'), {
          ...bankData,
          createdAt: new Date(),
        });
        setBanks([...banks, { id: docRef.id, ...bankData }]);
        showAlert('Success', 'Bank configuration added successfully');
      }

      // Reset form
      setFormData({
        name: '',
        accountHolderName: '',
        accountNumber: '',
        isActive: true,
        icon: 'account-balance',
      });
      setEditingId(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving bank:', error);
      showAlert('Error', 'Failed to save bank configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bank: Bank) => {
    setFormData({
      name: bank.name,
      accountHolderName: bank.accountHolderName,
      accountNumber: bank.accountNumber,
      isActive: bank.isActive,
      icon: bank.icon,
    });
    setEditingId(bank.id!);
    setShowAddForm(true);
  };

  const handleDelete = async (bankId: string) => {
    if (Platform.OS === 'web') {
      // Use browser's confirm dialog for web
      const confirmed = window.confirm('Are you sure you want to delete this bank configuration?');
      if (confirmed) {
        try {
          await deleteDoc(doc(db, 'banks', bankId));
          setBanks(banks.filter((bank) => bank.id !== bankId));
          window.alert('Bank configuration deleted successfully');
        } catch (error) {
          console.error('Error deleting bank:', error);
          window.alert('Failed to delete bank configuration');
        }
      }
    } else {
      // Use React Native Alert for mobile
      Alert.alert(
        'Confirm Delete',
        'Are you sure you want to delete this bank configuration?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await deleteDoc(doc(db, 'banks', bankId));
                setBanks(banks.filter((bank) => bank.id !== bankId));
                Alert.alert('Success', 'Bank configuration deleted successfully');
              } catch (error) {
                console.error('Error deleting bank:', error);
                Alert.alert('Error', 'Failed to delete bank configuration');
              }
            },
          },
        ]
      );
    }
  };

  const handleQuickAdd = (bankName: string, icon: string) => {
    setFormData({
      name: bankName,
      accountHolderName: '',
      accountNumber: '',
      isActive: true,
      icon,
    });
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      accountHolderName: '',
      accountNumber: '',
      isActive: true,
      icon: 'account-balance',
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.beeYellow} />
        <Text style={styles.loadingText}>Loading bank configurations...</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.adminPageBg }}>
      {/* ── Page Header ── */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.adminHeaderBg, paddingHorizontal: 28, paddingVertical: 18 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <View style={{ width: 40, height: 40, borderRadius: 11, backgroundColor: `${colors.sidebarAccent}22`, justifyContent: 'center', alignItems: 'center' }}>
            <Icon name="account-balance-wallet" size={22} color={colors.sidebarAccent} />
          </View>
          <View>
            <Text style={{ fontSize: 18, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB', letterSpacing: -0.3 }}>Bank Configurations</Text>
            <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: '#64748B', marginTop: 1 }}>
              {banks.length} bank{banks.length !== 1 ? 's' : ''} configured
            </Text>
          </View>
        </View>
        {!showAddForm && (
          <TouchableOpacity onPress={() => setShowAddForm(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.sidebarAccent, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 9 }}>
            <Icon name="add" size={16} color="#FFFFFF" />
            <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: '#FFFFFF' }}>Add Bank</Text>
          </TouchableOpacity>
        )}
      </View>

      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>

      {/* Add/Edit Form */}
      {showAddForm && (
        <View style={{ paddingHorizontal: 20, paddingTop: 16 }}>
          <View style={{ backgroundColor: colors.adminCardBg, borderRadius: 14, overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 3 } }) }}>
            <View style={{ backgroundColor: '#1E293B', paddingHorizontal: 20, paddingVertical: 14, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ fontSize: 15, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB' }}>{editingId ? 'Edit Bank' : 'Add New Bank'}</Text>
              <TouchableOpacity onPress={handleCancel} style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: '#FFFFFF18', justifyContent: 'center', alignItems: 'center' }}>
                <Icon name="close" size={16} color="#94A3B8" />
              </TouchableOpacity>
            </View>

            <View style={styles.formBody}>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.sidebarTextActive }]}>Bank Name</Text>
                <TextInput
                  style={[styles.input, { 
                    backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                    color: colors.sidebarTextActive
                  }]}
                  placeholder="e.g., Commercial Bank of Ethiopia"
                  value={formData.name}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                  placeholderTextColor={colors.adminMuted}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.sidebarTextActive }]}>Account Holder</Text>
                <TextInput
                  style={[styles.input, { 
                    backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                    color: colors.sidebarTextActive
                  }]}
                  placeholder="e.g., NibJobs Business Account"
                  value={formData.accountHolderName}
                  onChangeText={(text) =>
                    setFormData({ ...formData, accountHolderName: text })
                  }
                  placeholderTextColor={colors.adminMuted}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.sidebarTextActive }]}>Account Number</Text>
                <TextInput
                  style={[styles.input, { 
                    backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                    color: colors.sidebarTextActive
                  }]}
                  placeholder="Enter account number"
                  value={formData.accountNumber}
                  onChangeText={(text) =>
                    setFormData({ ...formData, accountNumber: text })
                  }
                  keyboardType="numeric"
                  placeholderTextColor={colors.adminMuted}
                />
              </View>

              <View style={styles.formGroup}>
                <View style={styles.switchContainer}>
                  <View>
                    <Text style={[styles.label, { color: colors.sidebarTextActive }]}>Active Status</Text>
                    <Text style={[styles.helpText, { color: colors.adminMuted }]}>
                      Only active banks are shown to users
                    </Text>
                  </View>
                  <Switch
                    value={formData.isActive}
                    onValueChange={(value) =>
                      setFormData({ ...formData, isActive: value })
                    }
                    trackColor={{ false: colors.adminMuted, true: '#10b981' }}
                    thumbColor={colors.white}
                  />
                </View>
              </View>
            </View>

            <View style={styles.formActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={handleCancel}
              >
                <Text style={[styles.cancelButtonText, { color: colors.adminMuted }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Icon name="check" size={20} color={colors.white} />
                    <Text style={styles.saveButtonText}>
                      {editingId ? 'Update Bank' : 'Save Bank'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Banks Grid */}
      <View style={{ paddingHorizontal: 20, paddingTop: 16, paddingBottom: 24 }}>
        {banks.length === 0 ? (
          <View style={{ alignItems: 'center', paddingVertical: 40, backgroundColor: colors.adminCardBg, borderRadius: 14, ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 } }) }}>
            <View style={{ width: 64, height: 64, borderRadius: 16, backgroundColor: colors.adminBorder, justifyContent: 'center', alignItems: 'center', marginBottom: 14 }}>
              <Icon name="account-balance" size={32} color={colors.adminMuted} />
            </View>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginBottom: 6 }}>No Payment Methods Yet</Text>
            <Text style={{ fontSize: 13, fontFamily: fonts.regular, color: colors.adminMuted, marginBottom: 16, textAlign: 'center', paddingHorizontal: 40 }}>Add your first bank account to start accepting payments</Text>
            <TouchableOpacity onPress={() => setShowAddForm(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#F59E0B', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10 }}>
              <Icon name="add" size={18} color="#1E293B" />
              <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: '#1E293B' }}>Add Your First Bank</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
            {banks.map((bank) => (
              <View key={bank.id} style={{ flex: 1, minWidth: 280, backgroundColor: colors.adminCardBg, borderRadius: 14, borderTopWidth: 3, borderTopColor: bank.isActive ? '#F59E0B' : colors.adminMuted, overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 } }) }}>
                {/* Bank Header */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, paddingBottom: 10 }}>
                  <View style={{ width: 44, height: 44, borderRadius: 11, backgroundColor: colors.sidebarHover, justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                    {(() => {
                      const iconKey = getBankIconKey(bank.id) || getBankIconKey(bank.name);
                      return iconKey ? (
                        <Image source={bankIcons[iconKey]} style={{ width: 32, height: 32 }} resizeMode="contain" />
                      ) : (
                        <Icon name={bank.icon || 'account-balance'} size={24} color={bank.isActive ? '#F59E0B' : colors.adminMuted} />
                      );
                    })()}
                  </View>
                  <Switch
                    value={bank.isActive}
                    onValueChange={async (value) => {
                      try {
                        const bankRef = doc(db, 'banks', bank.id!);
                        await updateDoc(bankRef, { isActive: value });
                        setBanks(banks.map(b => b.id === bank.id ? { ...b, isActive: value } : b));
                      } catch (error) {
                        console.error('Error updating bank status:', error);
                        showAlert('Error', 'Failed to update bank status');
                      }
                    }}
                    trackColor={{ false: '#64748b', true: '#10b981' }}
                    thumbColor={colors.white}
                    style={{ transform: [{ scale: 0.85 }] }}
                  />
                </View>

                {/* Bank Info */}
                <View style={{ paddingHorizontal: 16, paddingBottom: 12 }}>
                  <Text style={{ fontSize: 14, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginBottom: 4 }}>{bank.name}</Text>
                  <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted, marginBottom: 6 }}>{bank.accountHolderName}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Icon name="credit-card" size={13} color={colors.adminMuted} />
                    <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: colors.adminMuted }}>{bank.accountNumber}</Text>
                  </View>
                </View>

                {/* Status + Actions */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderTopWidth: 1, borderTopColor: colors.adminBorder }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: bank.isActive ? '#10B98120' : colors.adminBorder }}>
                    <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: bank.isActive ? '#10B981' : '#64748B' }} />
                    <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: bank.isActive ? '#10B981' : '#64748B' }}>{bank.isActive ? 'Active' : 'Inactive'}</Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    <TouchableOpacity onPress={() => handleEdit(bank)} style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: colors.adminBorder, justifyContent: 'center', alignItems: 'center' }}>
                      <Icon name="edit" size={16} color="#3B82F6" />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => handleDelete(bank.id!)} style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: colors.adminBorder, justifyContent: 'center', alignItems: 'center' }}>
                      <Icon name="delete-outline" size={16} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.warmGray,
    fontFamily: fonts.regular,
  },
  header: {
    padding: 24,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  headerIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    position: 'relative',
  },
  iconGlow: {
    position: 'absolute',
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fbbf24',
    opacity: 0.2,
    ...Platform.select({
      web: {
        filter: 'blur(20px)',
      } as any,
    }),
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    fontFamily: fonts.bold,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 15,
    fontFamily: fonts.medium,
    fontWeight: '500',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fbbf24',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
    elevation: 4,
    shadowColor: '#fbbf24',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
  },
  addButtonText: {
    color: '#1e293b',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fonts.medium,
  },
  formContainer: {
    padding: 20,
    paddingTop: 0,
  },
  formCard: {
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      } as any,
    }),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
  },
  formHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  formTitle: {
    fontSize: 20,
    fontWeight: '700',
    fontFamily: fonts.bold,
  },
  closeButton: {
    padding: 4,
  },
  formBody: {
    padding: 20,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    fontFamily: fonts.medium,
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
    fontFamily: fonts.regular,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      } as any,
    }),
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  helpText: {
    fontSize: 12,
    marginTop: 2,
    fontFamily: fonts.regular,
  },
  formActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  cancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 12,
    alignItems: 'center',
    backgroundColor: 'rgba(100, 116, 139, 0.1)',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fonts.medium,
  },
  saveButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fbbf24',
    padding: 14,
    borderRadius: 12,
    gap: 8,
    elevation: 4,
    shadowColor: '#fbbf24',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    color: '#1e293b',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: fonts.medium,
  },
  banksSection: {
    padding: 20,
    paddingTop: 0,
  },
  emptyState: {
    alignItems: 'center',
    padding: 48,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      } as any,
    }),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  emptyIcon: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    backgroundColor: 'rgba(100, 116, 139, 0.1)',
  },
  emptyStateText: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
    fontFamily: fonts.bold,
  },
  emptyStateSubtext: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
    fontFamily: fonts.regular,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fbbf24',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
    elevation: 4,
    shadowColor: '#fbbf24',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
  },
  emptyStateButtonText: {
    color: '#1e293b',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fonts.medium,
  },
  banksGrid: {
    gap: 16,
  },
  bankCard: {
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      } as any,
    }),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
  },
  bankHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  bankIconWrapper: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: 'rgba(251, 191, 36, 0.1)',
  },
  bankIconGlow: {
    position: 'absolute',
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fbbf24',
    opacity: 0.2,
    ...Platform.select({
      web: {
        filter: 'blur(16px)',
      } as any,
    }),
  },
  bankLogo: {
    width: 40,
    height: 40,
    zIndex: 1,
  },
  bankStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bankInfo: {
    padding: 20,
    gap: 8,
  },
  bankName: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
    fontFamily: fonts.bold,
  },
  bankDetail: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: fonts.medium,
  },
  accountNumberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  bankAccount: {
    fontSize: 13,
    fontFamily: 'monospace',
    fontWeight: '500',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginHorizontal: 20,
    marginBottom: 12,
    gap: 6,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: fonts.medium,
  },
  bankActions: {
    flexDirection: 'row',
    padding: 16,
    paddingTop: 0,
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(100, 116, 139, 0.1)',
    gap: 6,
  },
});

export default BankConfigurationContent;
